import type {
	LoginFlow,
	UiNodeGroupEnum,
	UpdateLoginFlowBody,
} from "@ory/kratos-client";
import type React from "react";
import { useState } from "react";
import {
	ContentContainer,
	ContentContainerWrapper,
} from "~/components/ContentContainer";
import { Flow, type Methods, type NodeDisabler } from "~/components/KratosFlow";
import { DefaultLayout } from "~/components/Layout";
import { validateTotpCode } from "~/validator";

const getMethodText = (method: Methods): string => {
	switch (method) {
		case "webauthn":
			return "Use Security key";
		case "totp":
			return "Use Authenticator";
		case "lookup_secret":
			return "Use Recovery Code";
		default:
			return "";
	}
};

const getNodeDisablers = (
	method: UiNodeGroupEnum,
): NodeDisabler<UpdateLoginFlowBody>[] => {
	switch (method) {
		case "totp":
			return [
				{
					target: { name: "method", value: "totp" },
					keys: ["totp_code" as keyof UpdateLoginFlowBody],
					allowEmpty: false,
					allowNotChanged: false,
					validators: [
						{
							key: "totp_code" as keyof UpdateLoginFlowBody,
							validate: (value): boolean => {
								if (typeof value !== "string") return false;
								return validateTotpCode(value);
							},
						},
					],
				},
			];
		case "lookup_secret":
			return [
				{
					target: { name: "method", value: "lookup_secret" },
					keys: ["lookup_secret" as keyof UpdateLoginFlowBody],
					allowEmpty: false,
					allowNotChanged: false,
				},
			];
		default:
			return [];
	}
};

interface Props {
	onSubmit: (values: UpdateLoginFlowBody) => Promise<void>;
	flow: LoginFlow;
}

const candidates: Methods[] = ["webauthn", "totp", "lookup_secret"];

export const Mfa: React.FC<Props> = ({ flow, onSubmit }) => {
	const [preferGroup, setPreferGroup] = useState<Methods>("webauthn");
	const hasGroup = (flow: LoginFlow, want: UiNodeGroupEnum) =>
		flow.ui.nodes.some(({ group }) => group === want);
	const groups = candidates
		.filter((group) => hasGroup(flow, group))
		.sort((a, b) => {
			if (a === preferGroup) return -1;
			if (b === preferGroup) return 1;
			return 0;
		});
	const notConfigured = candidates.filter((group) => !hasGroup(flow, group));

	return (
		<>
			<title>Login | TIER IV Account</title>
			<DefaultLayout>
				<ContentContainerWrapper>
					<ContentContainer>
						<h1 className="mt-10 text-center font-normal">
							MULTI-FACTOR AUTHENTICATION
						</h1>
						<Flow
							onSubmit={onSubmit}
							flow={flow}
							only={groups[0]}
							nodeDisablers={getNodeDisablers(groups[0])}
							hideGlobalMessagesTypes={["info"]}
						/>
						<div className="divider my-10">
							Change Method<span className="font-semibold">?</span>
						</div>
						{groups.length >= 2 &&
							groups.slice(1).map((method) => (
								<button
									type="button"
									key={method}
									className="block w-full text-center mt-6 cursor-pointer"
									onClick={() => setPreferGroup(method)}
								>
									{getMethodText(method)}
								</button>
							))}
						{notConfigured.map((method) => (
							<p
								key={method}
								className="text-center mt-6 cursor-not-allowed opacity-30"
							>
								{getMethodText(method)}
							</p>
						))}
					</ContentContainer>
				</ContentContainerWrapper>
			</DefaultLayout>
		</>
	);
};
